/**
 * 絵コンテの担当カードごとのシーン区間 (span_override 適用済み) とモーション上書きの鮮度を取る
 * (`GET /api/generation/scenarios/<sid>/storyboard/spans`・docs/features/brand-cut-motion-graphics-plan.md §10.13.1 契約 B)。
 *
 * ポーリングはしない。useMotionSyncStatus と同じく、区間や鮮度が変わり得るシーンの要約 (id / video_material_id /
 * video_input_mode / span_override / motion_overlay / scene_range) が変わったときだけ取り直す (react-query のキーに要約を含める)。
 * 境界の保存 (`putStoryboardBoundaries`) は同じ形を返すので `setStoryboardSpans` でそのまま差し替え、
 * 「利用しない」/ 効果音の更新後は `invalidateStoryboardSpans` で取り直す。
 */
import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchStoryboardSpans, type StoryboardSpansResponse } from '../../../../features/scenarios/api';

type SceneSummarySource = {
  id: string;
  order: number;
  video_material_id?: string | null;
  video_input_mode?: string | null;
  brand_cut_position?: 'start' | 'end' | null;
  generation_metadata?: Record<string, unknown> | null;
};

const STORYBOARD_SPANS_QUERY_KEY = 'storyboard-spans';

/** 取り直しの契機になるシーンの要約 (JSON 文字列)。 */
export function storyboardSpansSignature(scenes: readonly SceneSummarySource[]): string {
  return JSON.stringify(
    scenes.map((s) => {
      const meta = s.generation_metadata ?? {};
      return [
        s.id,
        s.order,
        s.video_material_id ?? null,
        s.video_input_mode ?? null,
        s.brand_cut_position ?? null,
        meta.scene_range ?? null,
        meta.span_override ?? null,
        meta.motion_overlay ?? null,
      ];
    }),
  );
}

export function storyboardSpansQueryKey(scenarioId: string, signature?: string): readonly unknown[] {
  return signature === undefined
    ? [STORYBOARD_SPANS_QUERY_KEY, scenarioId]
    : [STORYBOARD_SPANS_QUERY_KEY, scenarioId, signature];
}

export function useStoryboardSpans(
  scenarioId: string,
  scenes: readonly SceneSummarySource[],
  opts?: { enabled?: boolean },
) {
  const signature = useMemo(() => storyboardSpansSignature(scenes), [scenes]);
  const hasCardVideo = scenes.some((s) => !s.brand_cut_position && !!s.video_material_id);
  const query = useQuery<StoryboardSpansResponse>({
    queryKey: storyboardSpansQueryKey(scenarioId, signature),
    queryFn: () => fetchStoryboardSpans(scenarioId),
    // 担当カードの動画が無ければ区間は決まらない (BE も cards: [] を返す) ので呼ばない
    enabled: (opts?.enabled ?? true) && !!scenarioId && hasCardVideo,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
  return { ...query, signature, hasCardVideo };
}

/** 保存 API の応答 (spans と同じ形) で現在の値を差し替える。 */
export function setStoryboardSpans(
  queryClient: QueryClient,
  scenarioId: string,
  signature: string,
  response: StoryboardSpansResponse,
): void {
  queryClient.setQueryData(storyboardSpansQueryKey(scenarioId, signature), response);
}

/** このシナリオの spans を (要約を問わず) 取り直す。 */
export function invalidateStoryboardSpans(queryClient: QueryClient, scenarioId: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: storyboardSpansQueryKey(scenarioId) });
}
